import React, { useState, useRef } from 'react';
import './PageHeader.css';
import toast from 'react-hot-toast';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import { OpenSidebarIcon } from './Icons';
import { useAuth } from '@clerk/clerk-react';
import { usePostHog } from 'posthog-js/react';
import { getUserSubscriptionFromFirestore, deductCreditsFromSubscription } from '../services/firestoreService';
import * as Popover from '@radix-ui/react-popover';

interface PageHeaderProps {
  title?: string;
  onTitleChange?: (newTitle: string) => void;
  sidebarVisible?: boolean;
  onShowSidebar?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  selectedRowsCount?: number;
  selectedRows?: any[];
  columnConfigs?: any[];
  onEnrichmentComplete?: (rowId: string | number, updatedData: Record<string, any>) => void;
  onDeselectAll?: () => void;
  onAddToTemplates?: () => void;
  onSubscriptionRefresh?: () => Promise<void>;
}

const PageHeader: React.FC<PageHeaderProps> = ({ 
  title = "Untitled sheet", 
  onTitleChange,
  sidebarVisible,
  onShowSidebar,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  selectedRowsCount = 0,
  selectedRows = [],
  columnConfigs = [],
  onEnrichmentComplete,
  onDeselectAll,
  onAddToTemplates,
  onSubscriptionRefresh
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Enrichment state
  const [isEnriching, setIsEnriching] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Helper function to check if a row has at least one non-empty cell (excluding id)
  const hasAtLeastOneNonEmptyCell = (row: any): boolean => {
    return Object.keys(row).some(key => {
      if (key === 'id') return false; // Skip id field
      const value = row[key];
      return value !== '' && value !== null && value !== undefined;
    });
  };

  // Helper function to check if a row has at least one empty cell where column header name is not empty
  const hasEmptyCellWithHeaderName = (row: any): boolean => {
    return columnConfigs.some(config => {
      // Check if column has a non-empty name and the row has an empty value for this column
      const hasNonEmptyHeader = config.name && config.name.trim() !== '';
      const hasEmptyCell = !row[config.id] || row[config.id] === '' || row[config.id] === null || row[config.id] === undefined;
      return hasNonEmptyHeader && hasEmptyCell;
    });
  };

  // Function to determine if enrich button should be disabled and get tooltip message
  const getEnrichButtonState = (): { disabled: boolean; tooltipMessage: string } => {
    // Condition 1: If no selected rows - disabled
    if (selectedRowsCount === 0) {
      return { disabled: true, tooltipMessage: "Select rows to enrich data" };
    }

    if (isEnriching) {
      return { disabled: true, tooltipMessage: "Currently enriching..." };
    }

    // Check for eligible rows (rows that have BOTH data AND empty fields to fill)
    const eligibleRows = selectedRows.filter(row => {
      const hasData = hasAtLeastOneNonEmptyCell(row);
      const hasEmptyFields = hasEmptyCellWithHeaderName(row);
      return hasData && hasEmptyFields;
    });

    // If no rows are eligible for enrichment - disabled
    if (eligibleRows.length === 0) {
      // Determine the specific reason why no rows are eligible
      const rowsWithoutData = selectedRows.filter(row => !hasAtLeastOneNonEmptyCell(row));
      const rowsWithoutEmptyFields = selectedRows.filter(row => !hasEmptyCellWithHeaderName(row));
      
      if (rowsWithoutData.length === selectedRows.length) {
        return { 
          disabled: true, 
          tooltipMessage: "Add some data to selected rows first" 
        };
      } else if (rowsWithoutEmptyFields.length === selectedRows.length) {
        return { 
          disabled: true, 
          tooltipMessage: "Selected rows are already complete" 
        };
      } else {
        return { 
          disabled: true, 
          tooltipMessage: "No rows available for enrichment" 
        };
      }
    }

    return { disabled: false, tooltipMessage: "" };
  };

  const { disabled: enrichButtonDisabled, tooltipMessage } = getEnrichButtonState();
  const { userId } = useAuth();
  const posthog = usePostHog();

  const handleEnrichClick = async () => {
    if (enrichButtonDisabled) return;
    
    if (selectedRows.length === 0) {
      return;
    }

    // Filter rows that satisfy ALL conditions for enrichment
    const eligibleRows = selectedRows.filter(row => {
      const hasData = hasAtLeastOneNonEmptyCell(row);
      const hasEmptyFields = hasEmptyCellWithHeaderName(row);
      return hasData && hasEmptyFields;
    });

    if (eligibleRows.length === 0) {
      return;
    }

    // Track enrichment start
    posthog?.capture('enrichment_started', {
      selected_rows: selectedRows.length,
      eligible_rows: eligibleRows.length,
      total_columns: columnConfigs.length
    });

    // Calculate credits needed per row and filter affordable rows
    if (userId) {
      try {
        const subscription = await getUserSubscriptionFromFirestore(userId);
        
        if (!subscription) {
          toast.error('❌ Unable to verify your credit balance. Please try again.', {
            duration: 4000,
          });
          return;
        }

        // Calculate credits needed per row and determine which rows can be processed
        const rowsWithCredits: Array<{ row: any; creditsNeeded: number }> = [];
        
        eligibleRows.forEach(row => {
          let creditsForThisRow = 0;
          
          columnConfigs.forEach(config => {
            if (config.name && config.name.trim() !== '') {
              const cellContent = row[config.id];
              const isEmpty = !cellContent || cellContent === '';
              
              if (isEmpty) {
                creditsForThisRow += 1; // 1 credit per output column
              }
            }
          });
          
          rowsWithCredits.push({ row, creditsNeeded: creditsForThisRow });
        });

        // Sort rows by credits needed (ascending) to process cheaper rows first
        rowsWithCredits.sort((a, b) => a.creditsNeeded - b.creditsNeeded);

        // Determine which rows we can afford
        let remainingCredits = subscription.credits;
        const affordableRows: any[] = [];
        const unaffordableRows: any[] = [];

        rowsWithCredits.forEach(({ row, creditsNeeded }) => {
          if (remainingCredits >= creditsNeeded) {
            affordableRows.push(row);
            remainingCredits -= creditsNeeded;
          } else {
            unaffordableRows.push(row);
          }
        });

        // If no rows can be processed
        if (affordableRows.length === 0) {
          const minCreditsNeeded = Math.min(...rowsWithCredits.map(r => r.creditsNeeded));
          toast.error(`Not enough credits! You need at least ${minCreditsNeeded} credits but only have ${subscription.credits} remaining.`, {
            duration: 5000,
            style: {
              maxWidth: '400px',
            },
          });
          return;
        }

        // Update eligibleRows to only include affordable rows
        eligibleRows.length = 0;
        eligibleRows.push(...affordableRows);

        // Show appropriate messaging
        if (unaffordableRows.length > 0) {
          posthog?.capture('enrichment_partial_processing', {
            affordable_rows: affordableRows.length,
            unaffordable_rows: unaffordableRows.length,
            credits_available: subscription.credits
          });
          toast(`⚠️ Processing ${affordableRows.length} of ${eligibleRows.length + unaffordableRows.length} rows. ${unaffordableRows.length} rows skipped due to insufficient credits.`, {
            duration: 5000,
            style: {
              maxWidth: '450px',
              background: '#f59e0b',
              color: 'white',
            },
          });
        }
        
      } catch (error) {
        console.error('Error checking credits:', error);
        toast.error('❌ Unable to verify your credit balance. Please try again.', {
          duration: 4000,
        });
        return;
      }
    } else {
      toast.error('❌ Please sign in to use enrichment features.', {
        duration: 4000,
      });
      return;
    }

    setIsEnriching(true);
    
    // Clear row selection when enrichment starts
    if (onDeselectAll) {
      onDeselectAll();
    }
    
    try {
      // Helper function for delays
      const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

      // Step 1: Mark all empty cells with headers as "Queued..." for all eligible rows
      for (const row of eligibleRows) {
        const queuedData: Record<string, any> = {};
        
        columnConfigs.forEach(config => {
          if (config.name && config.name.trim() !== '') {
            const cellContent = row[config.id];
            const isEmpty = !cellContent || cellContent === '';
            
            if (isEmpty) {
              queuedData[config.id] = 'Queued...';
            }
          }
        });
        
        if (Object.keys(queuedData).length > 0 && onEnrichmentComplete) {
          onEnrichmentComplete(row.id, queuedData);
          await delay(100); // Small delay for UI updates
        }
      }

      // Step 2: Process eligible rows in batches of up to 10 simultaneously
      const { processRowData } = await import('../services/apiService');
      const BATCH_SIZE = 100;

      // Split eligible rows into batches
      const batches = [];
      for (let i = 0; i < eligibleRows.length; i += BATCH_SIZE) {
        batches.push(eligibleRows.slice(i, i + BATCH_SIZE));
      }

      // Process each batch in parallel
      for (const batch of batches) {
        let batchCreditsUsed = 0; // Track credits used in this batch
        
        // Process all rows in the current batch simultaneously
        const batchPromises = batch.map(async (row) => {
          let rowCreditsUsed = 0; // Track credits used for this specific row
          
          try {
            // Mark cells as "Generating..." before processing
            const generatingData: Record<string, any> = {};
            const columnsToProcess: string[] = [];
            
            columnConfigs.forEach(config => {
              if (config.name && config.name.trim() !== '') {
                const cellContent = row[config.id];
                const isEmpty = !cellContent || cellContent === '' || cellContent === 'Queued...';
                
                if (isEmpty) {
                  generatingData[config.id] = 'Generating...';
                  columnsToProcess.push(String(config.id));
                }
              }
            });
            
            if (Object.keys(generatingData).length > 0 && onEnrichmentComplete) {
              onEnrichmentComplete(row.id, generatingData);
              await delay(100); // Small delay for UI updates
            }

            // Prepare row data with column information
            const rowData: any = { id: row.id };
            
            columnConfigs.forEach(config => {
              const cellContent = row[config.id];
              
              // Include cells with actual content (not Generating/Queued status)
              if (cellContent && cellContent !== 'Generating...' && cellContent !== 'Queued...') {
                rowData[`col${config.id}`] = {
                  content: cellContent,
                  headerName: config.name || '',
                  promptDetails: config.options?.promptDetails || '',
                  formatOptions: config.options?.formatDetails || ''
                };
              } 
              // Include empty cells that we're processing (only if they have header names)
              else if (config.name && config.name.trim() !== '' && columnsToProcess.includes(String(config.id))) {
                rowData[`col${config.id}`] = {
                  content: '',
                  headerName: config.name,
                  promptDetails: config.options?.promptDetails || '',
                  formatOptions: config.options?.formatDetails || ''
                };
              }
            });

            // Call the enrichment API
            const response = await processRowData(rowData);
            
            if (response.success && response.data && response.data.processedData?.filledColumns) {
              // Extract filledColumns and convert back to grid format
              const filledColumns = response.data.processedData.filledColumns;
              const updatedRowData: Record<string, any> = {};
              
              // Convert from col{id} format back to original column id format and count non-empty results
              Object.keys(filledColumns).forEach(colKey => {
                const columnId = colKey.replace('col', '');
                const cellData = filledColumns[colKey];
                const content = cellData.content;
                updatedRowData[columnId] = content;
                
                // Count non-empty content for credit deduction
                if (content && content.trim() !== '') {
                  rowCreditsUsed += 1;
                }
              });
              
              // Reset any cells that weren't filled back to empty
              columnsToProcess.forEach(columnId => {
                if (!updatedRowData[columnId]) {
                  updatedRowData[columnId] = '';
                }
              });
              
              if (onEnrichmentComplete && Object.keys(updatedRowData).length > 0) {
                onEnrichmentComplete(row.id, updatedRowData);
              }
            } else {
              // Reset cells back to empty for this row
              const resetData: Record<string, any> = {};
              columnsToProcess.forEach(columnId => {
                resetData[columnId] = '';
              });
              
              if (onEnrichmentComplete && Object.keys(resetData).length > 0) {
                onEnrichmentComplete(row.id, resetData);
              }
            }

          } catch (rowError) {
            console.error(`Error processing row ${row.id}:`, rowError);
            
            // Reset cells back to empty on error
            const resetData: Record<string, any> = {};
            columnConfigs.forEach(config => {
              if (config.name && config.name.trim() !== '') {
                const cellContent = row[config.id];
                if (cellContent === 'Generating...' || cellContent === 'Queued...') {
                  resetData[config.id] = '';
                }
              }
            });
            
            if (onEnrichmentComplete && Object.keys(resetData).length > 0) {
              onEnrichmentComplete(row.id, resetData);
            }
          }
          
          return rowCreditsUsed; // Return credits used for this row
        });

        // Wait for all rows in the current batch to complete and collect credits used
        const batchCreditResults = await Promise.all(batchPromises);
        batchCreditsUsed = batchCreditResults.reduce((total, credits) => total + credits, 0);
        
        // Deduct credits from user's balance after successful batch completion
        if (batchCreditsUsed > 0 && userId) {
          try {
            const deductionSuccess = await deductCreditsFromSubscription(userId, batchCreditsUsed);
            if (deductionSuccess) {
              // Refresh sidebar credits display
              if (onSubscriptionRefresh) {
                await onSubscriptionRefresh();
              }
            } else {
              console.error(`Failed to deduct ${batchCreditsUsed} credits for batch`);
            }
          } catch (deductionError) {
            console.error('Error deducting credits:', deductionError);
          }
        }
        
        // Small delay between batches to avoid overwhelming the API
        if (batches.indexOf(batch) < batches.length - 1) {
          await delay(500);
        }
      }
      
      // Track enrichment completion
      posthog?.capture('enrichment_completed', {
        processed_rows: eligibleRows.length,
        total_batches: batches.length,
        success: true
      });
      
    } catch (error) {
      console.error('Enrichment error:', error);
      posthog?.capture('enrichment_failed', {
        error: String(error),
        selected_rows: selectedRows.length,
        eligible_rows: eligibleRows.length
      });
      
      // Reset all cells back to empty on general error
      for (const row of eligibleRows) {
        const resetData: Record<string, any> = {};
        columnConfigs.forEach(config => {
          if (config.name && config.name.trim() !== '') {
            const cellContent = row[config.id];
            if (cellContent === 'Generating...' || cellContent === 'Queued...') {
              resetData[config.id] = '';
            }
          }
        });
        
        if (onEnrichmentComplete && Object.keys(resetData).length > 0) {
          onEnrichmentComplete(row.id, resetData);
        }
      }
    } finally {
      setIsEnriching(false);
    }
  };

  const startEditingTitle = () => {
    setIsEditingTitle(true);
    setTitleInput(title);
    setTimeout(() => {
      if (titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.select();
      }
    }, 0);
  };

  const saveTitleChange = () => {
    setIsEditingTitle(false);
    if (onTitleChange && titleInput !== title) {
      posthog?.capture('sheet_title_changed', {
        old_title: title,
        new_title: titleInput
      });
      onTitleChange(titleInput);
    }
  };

  return (
    <div className="page-header">
      <div className="header-left">
        {!sidebarVisible && onShowSidebar && (
          <button className="show-sidebar-btn" onClick={onShowSidebar} title="Show sidebar">
            <OpenSidebarIcon width={20} height={20} />
          </button>
        )}
        {isEditingTitle ? (
          <div className="title-edit-container">
            <input
              ref={titleInputRef}
              type="text"
              className="title-input"
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              onBlur={saveTitleChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveTitleChange();
                if (e.key === 'Escape') {
                  setIsEditingTitle(false);
                  setTitleInput(title);
                }
              }}
            />
          </div>
        ) : (
          <h1 className="sheet-title" onClick={startEditingTitle}>{title}</h1>
        )}
        {onAddToTemplates && (
          <button 
            className="add-template-btn"
            onClick={onAddToTemplates}
            title="Add to Templates"
          >
            + Create Template
          </button>
        )}
        {/* <button className="icon-button">
          <MoreHorizOutlinedIcon style={{ fontSize: '16px' }} />
        </button> */}
      </div>
      <div className="header-right">
        <div className="navigation-buttons">
          <button 
            className="icon-button" 
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
          >
            <UndoOutlinedIcon style={{ fontSize: '16px' }} />
          </button>
          <button 
            className="icon-button"
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Y)"
          >
            <RedoOutlinedIcon style={{ fontSize: '16px' }} />
          </button>
        </div>
        <Popover.Root open={showTooltip && enrichButtonDisabled && tooltipMessage !== ""}>
          <Popover.Trigger asChild>
            <button 
              className={`enrich-button ${enrichButtonDisabled ? 'disabled' : ''}`}
              onClick={() => {
                if (enrichButtonDisabled) {
                  return;
                }
                handleEnrichClick();
              }}
              onMouseEnter={() => {
                if (enrichButtonDisabled && tooltipMessage !== "") {
                  setShowTooltip(true);
                }
              }}
              onMouseLeave={() => {
                setShowTooltip(false);
              }}
            >
              <AutoFixHighOutlinedIcon style={{ fontSize: '16px' }} />
              {isEnriching ? 'Enriching...' : 'Enrich'}
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="radix-popover-content"
              side="bottom"
              align="end"
              sideOffset={8}
            >
              <div className="popover-tooltip">
                {tooltipMessage}
              </div>
              <Popover.Arrow className="radix-popover-arrow" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    </div>
  );
};

export default PageHeader; 